import type { GPSummarySnapshot } from "../../../types/gpSummary";
import Card from "./Card";

interface SnapshotCardProps {
  snapshot: GPSummarySnapshot;
}

const COLOR_GOOD = "#6C7A52";
const COLOR_CONCERN = "#C97C7C";
const COLOR_MONITOR = "#a87f2a";

function trendArrow(trend: GPSummarySnapshot["moodTrend"]): string {
  if (trend === "improving") return "\u2191";
  if (trend === "declining") return "\u2193";
  if (trend === "stable") return "\u2192";
  return "";
}

function trendLabel(trend: GPSummarySnapshot["moodTrend"]): string {
  if (trend === "improving") return "Improving";
  if (trend === "declining") return "Declining";
  if (trend === "stable") return "Stable";
  return "Insufficient data";
}

interface TileProps {
  label: string;
  value: string;
  sub?: string;
  color: string;
  bgTint?: string;
}

function Tile({ label, value, sub, color, bgTint }: TileProps) {
  return (
    <div
      className="rounded-xl p-3.5 flex flex-col gap-1"
      style={{ background: bgTint ?? "#fafaf9" }}
    >
      <span className="text-[11px] uppercase tracking-wider text-stone-400 font-medium">{label}</span>
      <span className="text-lg font-bold leading-tight" style={{ color }}>{value}</span>
      {sub && <span className="text-xs text-stone-400">{sub}</span>}
    </div>
  );
}

export default function SnapshotCard({ snapshot }: SnapshotCardProps) {
  const {
    moodAverage,
    moodTrend,
    symptomsImproving,
    symptomsWorsening,
    symptomsResolved,
    redFlagCount,
    totalActivitiesCompleted,
    totalActivitiesAvailable,
    bleedingStoppedDay,
    journalEntryCount,
  } = snapshot;

  const moodColor = moodAverage >= 3.5 ? COLOR_GOOD : moodAverage >= 2.5 ? COLOR_MONITOR : COLOR_CONCERN;

  const positiveSymptoms = symptomsImproving + symptomsResolved;
  const symptomTotal = positiveSymptoms + symptomsWorsening;
  const symptomsColor = symptomTotal === 0 ? COLOR_GOOD
    : positiveSymptoms > symptomsWorsening ? COLOR_GOOD : COLOR_CONCERN;

  const actPct = totalActivitiesAvailable > 0
    ? Math.round((totalActivitiesCompleted / totalActivitiesAvailable) * 100)
    : 0;
  const actColor = actPct >= 70 ? COLOR_GOOD : actPct >= 40 ? COLOR_MONITOR : COLOR_CONCERN;

  const bleedingText = bleedingStoppedDay ? `Day ${bleedingStoppedDay}` : "None";
  const bleedingColor = !bleedingStoppedDay ? COLOR_GOOD
    : bleedingStoppedDay <= 35 ? COLOR_GOOD : COLOR_MONITOR;

  return (
    <Card title="Overview">
      <div className="grid grid-cols-2 gap-3">
        <Tile
          label="Mood"
          value={moodAverage > 0 ? `${moodAverage}/5` : "\u2014"}
          sub={`${trendArrow(moodTrend)} ${trendLabel(moodTrend)}`}
          color={moodColor}
        />
        <Tile
          label="Symptoms"
          value={symptomTotal > 0 ? `${positiveSymptoms}/${symptomTotal}` : "\u2014"}
          sub={symptomTotal > 0 ? `improving${symptomsWorsening > 0 ? ` \u00B7 ${symptomsWorsening} worsening` : ""}` : "No data"}
          color={symptomsColor}
        />
        <Tile
          label="Red Flags"
          value={redFlagCount > 0 ? `${redFlagCount}` : "None"}
          color={redFlagCount > 0 ? COLOR_CONCERN : COLOR_GOOD}
          bgTint={redFlagCount > 0 ? "#fdf5f5" : undefined}
        />
        <Tile
          label="Activities"
          value={totalActivitiesAvailable > 0 ? `${actPct}%` : "\u2014"}
          sub={totalActivitiesAvailable > 0 ? `${totalActivitiesCompleted}/${totalActivitiesAvailable} done` : "No data"}
          color={actColor}
        />
        <Tile
          label="Last Bleeding"
          value={bleedingText}
          sub={bleedingStoppedDay ? `Week ${Math.ceil(bleedingStoppedDay / 7)}` : "Not recorded"}
          color={bleedingColor}
        />
        <Tile
          label="Journal"
          value={`${journalEntryCount}`}
          sub="entries logged"
          color="#78716c"
        />
      </div>
    </Card>
  );
}
